/**
 * =============================================================================
 * BrandThemeProvider.tsx - Provider per Tema Dinamico White Label
 * =============================================================================
 * 
 * Questo componente inietta le variabili CSS del tema a runtime basandosi
 * sui valori definiti in brandConfig.ts. Questo permette di cambiare i colori
 * del sito modificando solo il file di configurazione.
 * 
 * COME FUNZIONA:
 * 1. Legge i colori HSL da brandConfig
 * 2. Li inietta come CSS custom properties nel :root
 * 3. Sovrascrive i valori di default definiti in index.css
 * 
 * @author CCFN Development Team
 * @version 1.0.0
 * =============================================================================
 */

import { useEffect } from "react";
import { brandConfig } from "@/config/brandConfig";
import { useTenantConfig } from "@/hooks/useFeatureFlag";
import { useTenantBrand } from "@/hooks/useTenantBrand";
import { useDynamicFavicon } from "@/hooks/useDynamicFavicon";

/** Fallback neutro stabile: non cambia identità a ogni render del provider. */
const NEUTRAL_COLORS = {
  primary: "#333333",   // Grigio scuro
  secondary: "#f0f0f0", // Grigio chiaro
  accent: "#666666",    // Grigio medio
};

/**
 * Converte un colore HEX in valori HSL (senza la funzione hsl())
 * Restituisce una stringa nel formato "H S% L%" per le variabili CSS
 */
function hexToHSL(hex: string): string {
  // Rimuovi il # se presente
  hex = hex.replace(/^#/, "");
  
  // Converti in RGB
  const r = parseInt(hex.substring(0, 2), 16) / 255;
  const g = parseInt(hex.substring(2, 4), 16) / 255;
  const b = parseInt(hex.substring(4, 6), 16) / 255;
  
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;
  
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    
    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
        break;
      case g:
        h = ((b - r) / d + 2) / 6;
        break;
      case b:
        h = ((r - g) / d + 4) / 6;
        break;
    }
  }
  
  // Converti in formato CSS: "H S% L%"
  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

/**
 * Genera una variante più chiara del colore (per hover, etc.)
 */
function lightenHSL(hslString: string, amount: number = 10): string {
  const parts = hslString.match(/(\d+)\s+(\d+)%\s+(\d+)%/);
  if (!parts) return hslString;
  
  const h = parseInt(parts[1]);
  const s = parseInt(parts[2]);
  const l = Math.min(100, parseInt(parts[3]) + amount);
  
  return `${h} ${s}% ${l}%`;
}

/**
 * Genera una variante più scura del colore
 */
function darkenHSL(hslString: string, amount: number = 10): string {
  const parts = hslString.match(/(\d+)\s+(\d+)%\s+(\d+)%/);
  if (!parts) return hslString;
  
  const h = parseInt(parts[1]);
  const s = parseInt(parts[2]);
  const l = Math.max(0, parseInt(parts[3]) - amount);
  
  return `${h} ${s}% ${l}%`;
}

/**
 * Provider che applica il tema del brand al documento
 * Deve essere inserito nel componente root (App.tsx)
 */
export function BrandThemeProvider({ children }: { children: React.ReactNode }) {
  const tenantConfig = useTenantConfig();
  const { isLni, slug, faviconUrl } = useTenantBrand();

  // La configurazione risolta prevale sui fallback statici, anche nel simulatore.
  useDynamicFavicon(slug, faviconUrl, isLni);

  useEffect(() => {
    // Per tenant non-LNI: usa colori dal DB, oppure fallback su NEUTRAL (non LNI Blue)
    // Per LNI: usa colori dal DB, oppure fallback su brandConfig
    const fallbackColors = isLni ? brandConfig.colors : NEUTRAL_COLORS;
    const primaryColor = (tenantConfig?.primary_color as string) || fallbackColors.primary;
    const secondaryColor = (tenantConfig?.secondary_color as string) || fallbackColors.secondary;
    const accentColor = (tenantConfig?.accent_color as string) || fallbackColors.accent;

    // Converti i colori HEX in HSL
    const primaryHSL = hexToHSL(primaryColor);
    const secondaryHSL = hexToHSL(secondaryColor);
    const accentHSL = hexToHSL(accentColor);
    
    // Ottieni il root element
    const root = document.documentElement;
    
    // ===== COLORI PRIMARI =====
    root.style.setProperty("--primary", primaryHSL);
    root.style.setProperty("--ring", primaryHSL);
    
    // Ocean deep (versione più scura del primary)
    root.style.setProperty("--ocean-deep", darkenHSL(primaryHSL, 10));
    
    // Ocean light (versione più chiara del primary)
    root.style.setProperty("--ocean-light", lightenHSL(primaryHSL, 20));
    
    // Wave (primary ancora più chiaro)
    root.style.setProperty("--wave", lightenHSL(primaryHSL, 35));
    
    // ===== COLORI SECONDARI =====
    root.style.setProperty("--secondary", secondaryHSL);
    root.style.setProperty("--sand", darkenHSL(secondaryHSL, 5));
    
    // Secondary foreground (testo scuro su sfondo secondario)
    const secondaryFg = darkenHSL(secondaryHSL, 65);
    root.style.setProperty("--secondary-foreground", secondaryFg);
    
    // ===== COLORI ACCENTO =====
    root.style.setProperty("--accent", accentHSL);
    root.style.setProperty("--sunset", accentHSL);
    
    // ===== SIDEBAR (basata su primary) =====
    root.style.setProperty("--sidebar-background", darkenHSL(primaryHSL, 10));
    root.style.setProperty("--sidebar-accent", primaryHSL);
    root.style.setProperty("--sidebar-border", lightenHSL(primaryHSL, 5));
    root.style.setProperty("--sidebar-ring", lightenHSL(primaryHSL, 20));
    
    // ===== GRADIENTI DINAMICI =====
    const gradientOcean = `linear-gradient(135deg, hsl(${primaryHSL}) 0%, hsl(${lightenHSL(primaryHSL, 20)}) 100%)`;
    const gradientSunset = `linear-gradient(135deg, hsl(${accentHSL}) 0%, hsl(${lightenHSL(accentHSL, 10)}) 100%)`;
    
    root.style.setProperty("--gradient-ocean", gradientOcean);
    root.style.setProperty("--gradient-sunset", gradientSunset);
    
    // ===== OMBRE DINAMICHE =====
    root.style.setProperty(
      "--shadow-ocean",
      `0 10px 40px -10px hsl(${primaryHSL} / 0.25)`
    );
    
    console.log("[BrandThemeProvider] Tema applicato:", {
      primary: primaryHSL,
      secondary: secondaryHSL,
      accent: accentHSL,
    });
    
  }, [tenantConfig, isLni]); // Ri-applica quando cambia la config del tenant
  
  return <>{children}</>;
}

export default BrandThemeProvider;
